import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../contexts/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Upload, Lightning, PencilSimple, Trash, ArrowsClockwise,
  Plus, CaretLeft, CaretRight, Check, FloppyDisk, ShieldCheck
} from "@phosphor-icons/react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { toast } from "sonner";

export default function CreateAssessment() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1: JD Input
  const [jdText, setJdText] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // Settings
  const [timeLimit, setTimeLimit] = useState(30);
  const [passingScore, setPassingScore] = useState(70);
  const [questionCount, setQuestionCount] = useState(10);
  const [allowReview, setAllowReview] = useState(true);
  const [allowChangeAnswers, setAllowChangeAnswers] = useState(true);
  const [requireFullscreen, setRequireFullscreen] = useState(true);
  const [enableWebcam, setEnableWebcam] = useState(false);

  // Step 2: Generated data
  const [jdAnalysis, setJdAnalysis] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [assessmentId, setAssessmentId] = useState(null);

  // Edit state
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [addingNew, setAddingNew] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      setLoading(true);
      const res = await axios.post(`${API}/upload/jd`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJdText(res.data.text);
      toast.success(`Extracted text from ${res.data.filename}`);
    } catch (err) {
      toast.error("Failed to extract text from file");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!jdText.trim()) { toast.error("Enter a job description"); return; }
    if (!title.trim()) { toast.error("Enter an assessment title"); return; }

    setLoading(true);
    try {
      // Create assessment first
      const createRes = await axios.post(`${API}/assessments`, {
        title, description,
        job_description: jdText,
        settings: {
          time_limit: timeLimit,
          passing_score: passingScore,
          question_count: questionCount,
          randomized_order: true,
          difficulty_distribution: { easy: 30, medium: 50, hard: 20 },
          allow_review: allowReview,
          allow_change_answers: allowChangeAnswers,
          require_fullscreen: requireFullscreen,
          enable_webcam: enableWebcam
        }
      }, { withCredentials: true });

      const aId = createRes.data.assessment_id;
      setAssessmentId(aId);

      // Generate questions
      const genRes = await axios.post(`${API}/assessments/${aId}/generate`, {}, { withCredentials: true });
      setJdAnalysis(genRes.data.jd_analysis);
      setQuestions(genRes.data.questions);
      setStep(2);
      toast.success("Assessment generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (qId) => {
    try {
      await axios.delete(`${API}/questions/${qId}`, { withCredentials: true });
      setQuestions(prev => prev.filter(q => q.question_id !== qId));
      toast.success("Question deleted");
    } catch { toast.error("Delete failed"); }
  };

  const handleRegenerate = async (qId) => {
    try {
      const res = await axios.post(`${API}/questions/${qId}/regenerate`, {}, { withCredentials: true });
      setQuestions(prev => prev.map(q => q.question_id === qId ? res.data : q));
      toast.success("Question regenerated");
    } catch { toast.error("Regeneration failed"); }
  };

  const startEdit = (idx) => {
    setEditIdx(idx);
    const q = questions[idx];
    setEditForm({
      question_text: q.question_text,
      question_type: q.question_type,
      options: q.options.map(o => ({ ...o })),
      difficulty: q.difficulty,
      skill_tag: q.skill_tag,
      explanation: q.explanation
    });
  };

  const saveEdit = async () => {
    const q = questions[editIdx];
    try {
      const res = await axios.put(`${API}/questions/${q.question_id}`, editForm, { withCredentials: true });
      setQuestions(prev => prev.map((item, i) => i === editIdx ? res.data : item));
      setEditIdx(null);
      setEditForm(null);
      toast.success("Question updated");
    } catch { toast.error("Update failed"); }
  };

  const handleAddQuestion = async () => {
    try {
      const res = await axios.post(`${API}/assessments/${assessmentId}/questions`, editForm, { withCredentials: true });
      setQuestions(prev => [...prev, res.data]);
      setAddingNew(false);
      setEditForm(null);
      toast.success("Question added");
    } catch { toast.error("Add failed"); }
  };

  const handleFinish = async () => {
    try {
      await axios.put(`${API}/assessments/${assessmentId}`, {
        settings: {
          time_limit: timeLimit, passing_score: passingScore,
          question_count: questions.length, randomized_order: true,
          difficulty_distribution: { easy: 30, medium: 50, hard: 20 },
          allow_review: allowReview,
          allow_change_answers: allowChangeAnswers,
          require_fullscreen: requireFullscreen,
          enable_webcam: enableWebcam
        }
      }, { withCredentials: true });
      toast.success("Assessment saved!");
      navigate(`/assessments/${assessmentId}/invite`);
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="min-h-screen pt-14 bg-white" data-testid="create-assessment-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        {/* Progress */}
        <div className="flex items-center gap-3 mb-8">
          {[1, 2].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-7 h-7 flex items-center justify-center text-xs font-bold
                ${step >= s ? 'bg-[#FF3366] text-white' : 'border border-black/20 text-[#52525B]'}`}>
                {step > s ? <Check size={14} /> : s}
              </div>
              <span className={`text-xs font-medium ${step >= s ? 'text-[#0A0A0A]' : 'text-[#52525B]'}`}>
                {s === 1 ? 'Job Description' : 'Review & Edit'}
              </span>
              {s < 2 && <div className="w-12 h-px bg-black/10" />}
            </div>
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="animate-fade-up" data-testid="step-1">
            <h1 className="font-heading font-bold text-3xl tracking-tight text-[#0A0A0A] mb-2">
              Create Assessment
            </h1>
            <p className="text-sm text-[#52525B] mb-8">
              Paste a job description or upload a file. Our AI will generate targeted MCQ questions.
            </p>

            <div className="space-y-5">
              <div>
                <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] mb-1.5 block">
                  Assessment Title *
                </label>
                <Input
                  value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="e.g., Senior Python Developer Assessment"
                  className="rounded-none border-black/20 focus:border-black focus:ring-0"
                  data-testid="assessment-title-input"
                />
              </div>

              <div>
                <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] mb-1.5 block">
                  Description
                </label>
                <Input
                  value={description} onChange={e => setDescription(e.target.value)}
                  placeholder="Brief description for candidates"
                  className="rounded-none border-black/20 focus:border-black focus:ring-0"
                  data-testid="assessment-desc-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">
                    Job Description *
                  </label>
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-1 text-xs font-medium text-[#FF3366] hover:text-[#E62E5C] transition-colors"
                    data-testid="upload-file-btn"
                  >
                    <Upload size={14} /> Upload PDF/DOCX/TXT
                  </button>
                  <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" className="hidden" onChange={handleFileUpload} />
                </div>
                <Textarea
                  value={jdText} onChange={e => setJdText(e.target.value)}
                  placeholder="Paste the full job description here..."
                  rows={12}
                  className="rounded-none border-black/20 focus:border-black focus:ring-0 resize-none text-sm"
                  data-testid="jd-textarea"
                />
              </div>

              {/* Settings row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] mb-1.5 block">Time (min)</label>
                  <Input type="number" value={timeLimit} onChange={e => setTimeLimit(Number(e.target.value))}
                    className="rounded-none border-black/20" data-testid="time-limit-input" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] mb-1.5 block">Pass Score (%)</label>
                  <Input type="number" value={passingScore} onChange={e => setPassingScore(Number(e.target.value))}
                    className="rounded-none border-black/20" data-testid="passing-score-input" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] mb-1.5 block">Questions</label>
                  <Input type="number" value={questionCount} onChange={e => setQuestionCount(Number(e.target.value))}
                    className="rounded-none border-black/20" data-testid="question-count-input" />
                </div>
              </div>

              {/* Proctoring settings */}
              <div className="border border-black/10 p-4 space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck size={14} className="text-[#52525B]" />
                  <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Proctoring & Security</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center justify-between text-xs text-[#0A0A0A]">
                    <span>Require Fullscreen</span>
                    <Switch checked={requireFullscreen} onCheckedChange={setRequireFullscreen} />
                  </label>
                  <label className="flex items-center justify-between text-xs text-[#0A0A0A]">
                    <span>Allow Answer Review</span>
                    <Switch checked={allowReview} onCheckedChange={setAllowReview} />
                  </label>
                  <label className="flex items-center justify-between text-xs text-[#0A0A0A]">
                    <span>Allow Changing Answers</span>
                    <Switch checked={allowChangeAnswers} onCheckedChange={setAllowChangeAnswers} />
                  </label>
                  <label className="flex items-center justify-between text-xs text-[#0A0A0A]">
                    <span>Webcam Proctoring</span>
                    <Switch checked={enableWebcam} onCheckedChange={setEnableWebcam} />
                  </label>
                </div>
              </div>

              <Button
                onClick={handleGenerate} disabled={loading}
                className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-6 py-3 h-auto font-semibold w-full"
                data-testid="generate-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating with AI...
                  </span>
                ) : (
                  <span className="flex items-center gap-2"><Lightning size={18} /> Generate Assessment</span>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="animate-fade-up" data-testid="step-2">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="font-heading font-bold text-3xl tracking-tight text-[#0A0A0A]">
                  Review Questions
                </h1>
                <p className="text-sm text-[#52525B] mt-1">
                  {questions.length} questions generated. Edit, delete, or add custom questions.
                </p>
              </div>
              <Button
                onClick={handleFinish}
                className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-5 py-2.5 h-auto font-semibold text-sm"
                data-testid="save-assessment-btn"
              >
                <FloppyDisk size={16} className="mr-1.5" /> Save & Invite
              </Button>
            </div>

            {/* JD Analysis */}
            {jdAnalysis && (
              <div className="border border-black/10 p-5 mb-6 bg-[#F8F9FA]" data-testid="jd-analysis">
                <h3 className="font-heading font-semibold text-sm text-[#0A0A0A] mb-3">
                  {jdAnalysis.title} — {jdAnalysis.seniority_level} level
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {[...jdAnalysis.skills, ...jdAnalysis.technologies].map((s, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 bg-white border border-black/10 font-medium text-[#0A0A0A]">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Questions list */}
            <div className="space-y-3">
              {questions.map((q, idx) => (
                <div key={q.question_id} className="border border-black/10 hover:shadow-md transition-all" data-testid={`question-card-${idx}`}>
                  {editIdx === idx ? (
                    /* Edit mode */
                    <div className="p-5 space-y-3">
                      <Textarea value={editForm.question_text}
                        onChange={e => setEditForm({ ...editForm, question_text: e.target.value })}
                        className="rounded-none border-black/20 text-sm" rows={2}
                        data-testid="edit-question-text" />
                      <div className="grid grid-cols-2 gap-2">
                        {editForm.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <input type="checkbox" checked={opt.is_correct}
                              onChange={() => {
                                const newOpts = [...editForm.options];
                                if (editForm.question_type === "single") {
                                  newOpts.forEach(o => o.is_correct = false);
                                }
                                newOpts[oi].is_correct = !newOpts[oi].is_correct;
                                setEditForm({ ...editForm, options: newOpts });
                              }}
                              className="w-4 h-4" />
                            <Input value={opt.text}
                              onChange={e => {
                                const newOpts = [...editForm.options];
                                newOpts[oi].text = e.target.value;
                                setEditForm({ ...editForm, options: newOpts });
                              }}
                              className="rounded-none border-black/20 text-sm flex-1"
                              data-testid={`edit-option-${oi}`} />
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={editForm.difficulty}
                          onValueChange={v => setEditForm({ ...editForm, difficulty: v })}>
                          <SelectTrigger className="rounded-none border-black/20 w-32" data-testid="edit-difficulty">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            <SelectItem value="easy">Easy</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="hard">Hard</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input value={editForm.skill_tag}
                          onChange={e => setEditForm({ ...editForm, skill_tag: e.target.value })}
                          placeholder="Skill tag" className="rounded-none border-black/20 text-sm flex-1" />
                        <Button onClick={saveEdit} className="bg-[#0A0A0A] text-white rounded-none px-4 h-9 text-xs" data-testid="save-edit-btn">
                          Save
                        </Button>
                        <Button variant="ghost" onClick={() => { setEditIdx(null); setEditForm(null); }} className="rounded-none h-9 text-xs" data-testid="cancel-edit-btn">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold text-[#52525B] tabular-nums">Q{idx + 1}</span>
                            <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase
                              ${q.difficulty === 'easy' ? 'bg-[#10B981]/10 text-[#10B981]' :
                                q.difficulty === 'hard' ? 'bg-[#EF4444]/10 text-[#EF4444]' :
                                'bg-[#F59E0B]/10 text-[#F59E0B]'}`}>
                              {q.difficulty}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#F8F9FA] text-[#52525B] font-medium">
                              {q.skill_tag}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#F8F9FA] text-[#52525B] font-medium">
                              {q.question_type}
                            </span>
                          </div>
                          <p className="text-sm text-[#0A0A0A] mb-3">{q.question_text}</p>
                          <div className="grid grid-cols-2 gap-1.5">
                            {q.options?.map((opt, oi) => (
                              <div key={oi}
                                className={`text-xs px-3 py-1.5 border ${opt.is_correct ? 'border-[#10B981] bg-[#10B981]/5 text-[#10B981]' : 'border-black/10 text-[#52525B]'}`}>
                                {opt.text} {opt.is_correct && <Check size={12} className="inline ml-1" />}
                              </div>
                            ))}
                          </div>
                          {q.explanation && (
                            <p className="text-xs text-[#52525B] mt-2 italic">Explanation: {q.explanation}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button onClick={() => startEdit(idx)} className="p-1.5 hover:bg-black/5" data-testid={`edit-q-${idx}`}>
                                <PencilSimple size={14} className="text-[#52525B]" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Edit question</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button onClick={() => handleRegenerate(q.question_id)} className="p-1.5 hover:bg-black/5" data-testid={`regen-q-${idx}`}>
                                <ArrowsClockwise size={14} className="text-[#52525B]" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Regenerate</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button onClick={() => handleDeleteQuestion(q.question_id)} className="p-1.5 hover:bg-[#EF4444]/10" data-testid={`delete-q-${idx}`}>
                                <Trash size={14} className="text-[#EF4444]" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Delete question</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Add question */}
              {addingNew ? (
                <div className="border border-black/10 p-5 space-y-3 bg-[#F8F9FA]" data-testid="add-question-form">
                  <Textarea value={editForm?.question_text || ""}
                    onChange={e => setEditForm({ ...editForm, question_text: e.target.value })}
                    placeholder="Question text"
                    className="rounded-none border-black/20 text-sm" rows={2} data-testid="new-question-text" />
                  <div className="grid grid-cols-2 gap-2">
                    {(editForm?.options || []).map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input type="checkbox" checked={opt.is_correct}
                          onChange={() => {
                            const newOpts = [...editForm.options];
                            newOpts[oi].is_correct = !newOpts[oi].is_correct;
                            setEditForm({ ...editForm, options: newOpts });
                          }} className="w-4 h-4" />
                        <Input value={opt.text}
                          onChange={e => {
                            const newOpts = [...editForm.options];
                            newOpts[oi].text = e.target.value;
                            setEditForm({ ...editForm, options: newOpts });
                          }}
                          placeholder={`Option ${oi + 1}`}
                          className="rounded-none border-black/20 text-sm flex-1" />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={editForm?.difficulty || "medium"}
                      onValueChange={v => setEditForm({ ...editForm, difficulty: v })}>
                      <SelectTrigger className="rounded-none border-black/20 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input value={editForm?.skill_tag || ""} onChange={e => setEditForm({ ...editForm, skill_tag: e.target.value })}
                      placeholder="Skill tag" className="rounded-none border-black/20 text-sm flex-1" />
                    <Button onClick={handleAddQuestion} className="bg-[#FF3366] text-white rounded-none px-4 h-9 text-xs" data-testid="confirm-add-btn">Add</Button>
                    <Button variant="ghost" onClick={() => { setAddingNew(false); setEditForm(null); }} className="rounded-none h-9 text-xs" data-testid="cancel-add-btn">Cancel</Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setAddingNew(true);
                    setEditForm({
                      question_text: "", question_type: "single",
                      options: [
                        { text: "", is_correct: false }, { text: "", is_correct: false },
                        { text: "", is_correct: false }, { text: "", is_correct: false }
                      ],
                      difficulty: "medium", skill_tag: "", explanation: ""
                    });
                  }}
                  className="w-full border border-dashed border-black/20 py-3 text-sm text-[#52525B] hover:text-[#0A0A0A] hover:border-black/40 transition-colors flex items-center justify-center gap-2"
                  data-testid="add-question-btn"
                >
                  <Plus size={16} /> Add Custom Question
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
